import { useState } from "react";
import { Copy, Loader2, ShieldAlert, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, apiError } from "@/lib/api";

const Line = ({ label, value, testId }) => (
  <div>
    <div className="label-caps text-[9px]">{label}</div>
    <div className="font-data text-xs" data-testid={testId}>
      {value || "—"}
    </div>
  </div>
);

/** One set of usecases inside a unit whose names read as the same work. */
const Group = ({ group, canSettle, onDecide, busy }) => {
  const [keep, setKeep] = useState(group.members[0]?.code || "");
  const [note, setNote] = useState("");
  return (
    <div
      className="border-2 border-[hsl(25_90%_48%)] bg-card p-4"
      data-testid={`dup-group-${group.signature}`}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <span className="label-caps text-[hsl(25_90%_48%)]">
            {group.plant} · {group.size} usecases read as the same work
          </span>
          <div className="font-data mt-1 text-[11px] text-muted-foreground">{group.reads_as}</div>
        </div>
        {canSettle && (
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={!!busy}
              data-testid={`dup-not-${group.signature}`}
              onClick={() => onDecide(group, "not_duplicate", "", note)}
            >
              Not duplicates — both stay
            </Button>
            <Button
              size="sm"
              disabled={!!busy || !keep}
              data-testid={`dup-drop-${group.signature}`}
              onClick={() => onDecide(group, "duplicate", keep, note)}
            >
              {busy === group.signature ? (
                <Loader2 className="mr-2 h-3 w-3 animate-spin" />
              ) : (
                <Copy className="mr-2 h-3 w-3" />
              )}
              Duplicate — keep {keep}, drop the rest
            </Button>
          </div>
        )}
      </div>

      <div className="mt-3 grid gap-2 lg:grid-cols-2">
        {group.members.map((m) => (
          <label
            key={m.code}
            data-testid={`dup-member-${group.signature}-${m.code}`}
            className={`block cursor-pointer border p-3 transition-colors duration-200 ${
              keep === m.code ? "border-primary bg-primary/5" : "border-border hover:bg-muted"
            }`}
          >
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="font-data text-[11px] text-muted-foreground">
                  {m.code}
                  {m.dropped ? " · already dropped" : ""}
                </div>
                <div className="text-sm font-semibold leading-snug">{m.title}</div>
              </div>
              {canSettle && (
                <input
                  type="radio"
                  name={`keep-${group.signature}`}
                  checked={keep === m.code}
                  onChange={() => setKeep(m.code)}
                  data-testid={`dup-keep-${group.signature}-${m.code}`}
                  className="mt-1 h-4 w-4 accent-[hsl(var(--primary))]"
                />
              )}
            </div>
            <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
              <Line label="Status" value={m.status} />
              <Line label="Identified" value={m.identified_on} />
              <Line label="Starts" value={m.start_on} />
              <Line label="Expected ₹ Cr" value={m.expected_cr ? m.expected_cr.toFixed(2) : "—"} />
              <Line label="Sub-stage" value={m.phase} />
              <Line label="Dept / Mine" value={m.dept_shop} />
              <Line label="Owner" value={m.owner_name} />
              <Line label="Entries filled" value={`${m.filled}`} />
            </div>
          </label>
        ))}
      </div>

      {canSettle && (
        <Input
          className="mt-3 h-10 rounded-none"
          placeholder="A note, if it helps whoever reads this later…"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          data-testid={`dup-note-${group.signature}`}
        />
      )}
    </div>
  );
};

/** Potential duplicates, and what has already been settled. */
export const DuplicateGroups = ({ plant, q, onChanged }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [open, setOpen] = useState(false);

  const load = () =>
    api
      .get("/duplicates", { params: { plant, q } })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiError(e)));

  // Loaded on first open and after every ruling, so the block costs nothing until it is wanted.
  const toggle = () => {
    setOpen((was) => !was);
    if (!data) load();
  };

  const decide = async (group, decision, keep, note) => {
    setBusy(group.signature);
    try {
      const { data: said } = await api.post("/duplicates/decide", {
        codes: group.members.map((m) => m.code),
        decision,
        keep,
        note,
      });
      toast.success(
        said.decision === "duplicate"
          ? `${said.kept} stays; ${said.dropped.join(", ")} dropped as a duplicate.`
          : "Settled as not duplicates — both stay on the register.",
      );
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  const undo = async (ruling) => {
    setBusy(ruling.id);
    try {
      await api.delete(`/duplicates/${ruling.id}`);
      toast.success("The ruling is taken back.");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  const totals = data?.totals || {};
  return (
    <div className="mt-8 border border-border bg-card" data-testid="duplicates-block">
      <button
        type="button"
        onClick={toggle}
        data-testid="duplicates-toggle"
        className="flex w-full flex-wrap items-center justify-between gap-3 p-5 text-left transition-colors duration-200 hover:bg-muted"
      >
        <div>
          <div className="label-caps text-[hsl(25_90%_48%)]">
            <ShieldAlert className="mr-1.5 inline h-3.5 w-3.5" />
            Potential duplicates
          </div>
          <h2 className="mt-1 font-display text-lg font-bold">
            The same work entered twice in one unit
          </h2>
          <p className="mt-1 max-w-2xl text-xs text-muted-foreground">
            Names that read as the same work inside one unit. Clear a group as{" "}
            <b>not duplicates</b> and both stay, for good; settle it as a <b>duplicate</b> and the
            one you keep stands while the rest are Dropped with the reason — nothing is destroyed.
          </p>
        </div>
        <div className="text-right">
          <div
            className="font-data text-3xl font-black tabular-nums text-[hsl(25_90%_48%)]"
            data-testid="dup-count"
          >
            {data ? totals.groups ?? 0 : "·"}
          </div>
          <div className="label-caps text-[9px]">
            {open ? "press to close" : "groups to look at"}
          </div>
        </div>
      </button>

      {open && (
        <div className="border-t border-border p-5">
          {!data && (
            <div className="flex h-24 items-center justify-center">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          )}
          {data && (
            <>
              <p className="text-[11px] text-muted-foreground">{data.note}</p>
              <div className="mt-4 space-y-4" data-testid="dup-groups">
                {(data.groups || []).map((g) => (
                  <Group
                    key={g.signature}
                    group={g}
                    canSettle={data.can_settle}
                    busy={busy}
                    onDecide={decide}
                  />
                ))}
                {!(data.groups || []).length && (
                  <p className="py-6 text-center text-sm text-muted-foreground" data-testid="dup-empty">
                    No two usecases in this view read as the same work.
                  </p>
                )}
              </div>

              {!!(data.settled || []).length && (
                <div className="mt-6 border-t border-border pt-4" data-testid="dup-settled">
                  <div className="label-caps">
                    Already settled · {(data.settled || []).length}
                  </div>
                  <ul className="mt-2 space-y-2">
                    {data.settled.map((g) => (
                      <li
                        key={g.signature}
                        className="flex flex-wrap items-center justify-between gap-2 border border-border p-3 text-xs"
                        data-testid={`dup-settled-${g.signature}`}
                      >
                        <span>
                          <span className="font-data">{g.members.map((m) => m.code).join(" · ")}</span>
                          <span className="text-muted-foreground">
                            {" — "}
                            {g.ruling.decision === "duplicate"
                              ? `duplicate; ${g.ruling.kept} kept, ${(g.ruling.dropped || []).join(", ")} dropped`
                              : "not duplicates; both stay"}
                            {" · "}
                            {g.ruling.by} on {g.ruling.on}
                            {g.ruling.note ? ` · ${g.ruling.note}` : ""}
                          </span>
                        </span>
                        {data.can_settle && (
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={busy === g.ruling.id}
                            data-testid={`dup-undo-${g.signature}`}
                            onClick={() => undo(g.ruling)}
                          >
                            <Undo2 className="mr-1.5 h-3 w-3" /> Take it back
                          </Button>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};
